import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ForensicSparkline from './ForensicSparkline';
import ForensicExplainModal from './ForensicExplainModal';
import ForensicPlainLanguageBlock from './ForensicPlainLanguageBlock';
import { formatForensicValue, zoneClass } from '../utils/forensicDisplay';

const formatDriver = (driver) => {
    if (!driver) return '';
    const from = driver.from != null ? Number(driver.from).toLocaleString('en-IN') : null;
    const to = driver.to != null ? Number(driver.to).toLocaleString('en-IN') : null;
    const pct = driver.changePct != null
        ? ` (${driver.changePct >= 0 ? '+' : ''}${Number(driver.changePct).toFixed(1)}%)`
        : '';
    if (from != null && to != null) {
        return `${driver.label} ${from}${driver.unit ? ` ${driver.unit}` : ''} → ${to}${pct}`;
    }
    if (to != null) {
        return `${driver.label} ${to}${driver.unit ? ` ${driver.unit}` : ''}${pct}`;
    }
    return driver.label;
};

const formatTestNumber = (value, unit) => {
    if (value == null || Number.isNaN(Number(value))) return null;
    const n = Number(value);
    if (unit === 'days') return `${n.toFixed(0)} days`;
    if (unit === '%') return `${n.toFixed(1)}%`;
    if (unit === 'Cr') return `${n.toLocaleString('en-IN')} Cr`;
    return n.toLocaleString('en-IN');
};

const formatMontierTest = (test) => {
    const from = formatTestNumber(test.from, test.unit);
    const to = formatTestNumber(test.to, test.unit);
    const pct = test.changePct != null
        ? ` (${test.changePct >= 0 ? '+' : ''}${Number(test.changePct).toFixed(1)}%)`
        : '';
    if (from && to) return `${from} → ${to}${pct}`;
    if (to) return `${to}${pct}`;
    return '';
};

const testState = (test) => {
    if (!test?.available) return 'unavailable';
    return test.fired ? 'fired' : 'clear';
};

const testStateLabel = (state) => {
    if (state === 'fired') return 'FIRED';
    if (state === 'clear') return 'not fired';
    return 'unavailable';
};

export default function ForensicEvidenceCard({ card, onBeneishOpen, onAskAgent }) {
    const [explain, setExplain] = useState(null);
    if (!card) return null;
    const clickable = card.name === 'beneishMScore' && Array.isArray(card.components) && card.components.length > 0;
    const median = card.comparison?.value;
    const montierTests = card.name === 'montierCScore' && Array.isArray(card.tests) ? card.tests : [];
    const showDrivers = !montierTests.length && card.drivers?.length > 0;
    const scoreExplain = card.scoreExplain || null;
    const showAgent = Boolean(onAskAgent) && card.value != null;

    return (
        <article
            className={`forensic-card ${zoneClass(card.zone)}`}
            data-metric={card.name}
        >
            <header className="forensic-card__head">
                <div className="forensic-card__name-row">
                    <h4 className="forensic-card__name">{card.displayName}</h4>
                    {scoreExplain && (
                        <button
                            type="button"
                            className="forensic-info"
                            aria-label={`How ${card.displayName} is calculated`}
                            onClick={() => setExplain(scoreExplain)}
                        >
                            <FontAwesomeIcon icon="circle-info" />
                        </button>
                    )}
                    {showAgent && (
                        <button
                            type="button"
                            className="forensic-info forensic-agent"
                            aria-label={`Ask forensic agent about ${card.displayName}`}
                            onClick={() => onAskAgent({ metric: card.name, title: card.displayName })}
                        >
                            <FontAwesomeIcon icon="robot" />
                        </button>
                    )}
                </div>
                <span className={`forensic-badge ${zoneClass(card.zone)}`}>
                    {card.thresholdLabel || card.severity}
                </span>
            </header>
            <div className="forensic-card__value-row">
                <span className="forensic-card__value">
                    {formatForensicValue(card.name, card.value, card.unit)}
                </span>
                {median != null && (
                    <span className="forensic-card__median">
                        vs {card.comparison?.label || '10-year self median'} {formatForensicValue(card.name, median, card.unit)}
                    </span>
                )}
            </div>
            <ForensicSparkline series={card.sparkline} median={median} zone={card.zone} />
            {showDrivers && (
                <p className="forensic-card__drivers">
                    {card.drivers.slice(0, 3).map(formatDriver).filter(Boolean).join(' · ')}
                </p>
            )}
            {montierTests.length > 0 && (
                <ul className="forensic-montier-tests">
                    {montierTests.map((test) => {
                        const state = testState(test);
                        const numbers = formatMontierTest(test);
                        const context = (test.context || []).map(formatDriver).filter(Boolean).join(' · ');
                        return (
                            <li
                                key={test.name}
                                className={`forensic-montier-tests__item forensic-montier-tests__item--${state}`}
                            >
                                <span className="forensic-montier-tests__name">
                                    {test.displayName}
                                    {test.explain && (
                                        <button
                                            type="button"
                                            className="forensic-info"
                                            aria-label={`How ${test.displayName} is calculated`}
                                            onClick={() => setExplain(test.explain)}
                                        >
                                            <FontAwesomeIcon icon="circle-info" />
                                        </button>
                                    )}
                                </span>
                                {numbers && <span className="forensic-montier-tests__numbers">{numbers}</span>}
                                {context && <span className="forensic-montier-tests__context">{context}</span>}
                                {test.note && <span className="forensic-montier-tests__note">{test.note}</span>}
                                <span className="forensic-montier-tests__state">{testStateLabel(state)}</span>
                            </li>
                        );
                    })}
                </ul>
            )}
            <ForensicPlainLanguageBlock
                plain={card.plainLanguage || card.plain_language}
                technical={card.whyItMatters}
            />
            {clickable && (
                <button
                    type="button"
                    className="forensic-card__drill"
                    onClick={() => onBeneishOpen?.(card)}
                >
                    Beneish component breakdown
                </button>
            )}
            <ForensicExplainModal explain={explain} onClose={() => setExplain(null)} />
        </article>
    );
}
